import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Shield, ArrowRight, BarChart3, CheckCircle2, HelpCircle, Link2, EyeOff, BarChart, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const features = [
  "Connect your Stripe or Polar account in seconds",
  "Customer data is automatically anonymized & secured",
  "Track MRR, churn, and revenue trends in real time",
];

const steps = [
  {
    icon: Link2,
    title: "Connect Your Payment Provider",
    description: "Create a project in your dashboard and connect your Stripe or Polar account with a read-only API key. Setup takes under 2 minutes.",
  },
  {
    icon: EyeOff,
    title: "Data Is Synced & Secured",
    description: "TrueMRR pulls subscription data directly from the API. All customer PII is automatically anonymized — your data stays private.",
  },
  {
    icon: BarChart,
    title: "Get Actionable Revenue Insights",
    description: "Instantly see your MRR, ARR, churn rate, ARPU, LTV, and plan breakdowns — all in a clean, secure analytics dashboard.",
  },
];

const faqs = [
  {
    q: "How does TrueMRR connect to my Stripe account?",
    a: "TrueMRR uses a read-only Stripe restricted key to pull subscription and revenue data. We never access payment methods, bank details, or perform write operations. You create a restricted key with exactly 6 read-only scopes, and TrueMRR handles the rest.",
  },
  {
    q: "Is my customer data safe?",
    a: "Yes. TrueMRR automatically anonymizes all customer personally identifiable information (PII) before storing any data. Customer IDs are replaced with anonymous hashes, and no names, emails, or addresses are ever stored.",
  },
  {
    q: "Do I need technical knowledge to set up TrueMRR?",
    a: "No. Just create an account, generate a read-only API key from your Stripe or Polar dashboard, and paste it in. TrueMRR syncs your data automatically — no engineering required.",
  },
  {
    q: "How much does TrueMRR cost?",
    a: "TrueMRR costs $499 per month for unlimited projects, data connections, and revenue reports. You can also invite team members to share read-only dashboards at no extra cost.",
  },
  {
    q: "What SaaS metrics does TrueMRR track?",
    a: "TrueMRR tracks Monthly Recurring Revenue (MRR), Annual Run Rate (ARR), gross churn rate, active subscriptions, Average Revenue Per User (ARPU), estimated Lifetime Value (LTV), net revenue retention, and plan tier breakdowns — all directly from the Stripe or Polar API.",
  },
  {
    q: "What payment processors does TrueMRR support?",
    a: "TrueMRR currently supports Stripe and Polar as data sources. Each source is independently synced and labeled in the dashboard and reports.",
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Index = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [siteMode, setSiteMode] = useState<"live" | "waitlist" | null>(null);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  useEffect(() => {
    supabase
      .from("site_settings" as any)
      .select("value")
      .eq("key", "site_mode")
      .single()
      .then(({ data }: any) => {
        setSiteMode((data?.value as "live" | "waitlist") || "live");
      });
  }, []);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setWaitlistLoading(true);
    const { error } = await supabase.from("waitlist" as any).insert({ email: waitlistEmail.trim() } as any);
    if (error) {
      if (error.code === "23505") {
        toast({ title: "Already on the list!", description: "You're already on our waitlist. We'll be in touch soon." });
      } else {
        toast({ title: "Error", description: "Could not join waitlist. Please try again.", variant: "destructive" });
      }
    } else {
      toast({ title: "You're on the list!", description: "We'll notify you when your workspace is ready." });
      setWaitlistEmail("");
    }
    setWaitlistLoading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Site Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" aria-hidden="true" />
            <span className="text-sm font-semibold">TrueMRR</span>
          </div>
          <nav aria-label="Site navigation" className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/help")}>
              Help
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/login")}>
              Sign In
            </Button>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="flex items-center justify-center px-4 pt-20 pb-16" aria-labelledby="hero-heading">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-xl"
          >
            <div className="mx-auto mb-6 h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center">
              <Shield className="h-7 w-7 text-primary" aria-hidden="true" />
            </div>

            <h1 id="hero-heading" className="text-3xl font-semibold mb-3">
              SaaS Revenue Analytics Dashboard
            </h1>
            <p className="text-muted-foreground mb-6 text-sm leading-relaxed max-w-md mx-auto">
              Track your Stripe MRR, churn, and growth in one place. TrueMRR is the
              secure revenue analytics platform built for SaaS founders and product teams.
              Connect your payment provider — get real-time insights instantly.
            </p>

            <ul className="text-left max-w-sm mx-auto space-y-2 mb-8" aria-label="Key features of SaaS revenue analytics">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {siteMode === "waitlist" ? (
              <div className="space-y-3">
                <form onSubmit={handleWaitlistSubmit} className="flex flex-col sm:flex-row items-center justify-center gap-2 max-w-md mx-auto">
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    required
                    className="h-11"
                  />
                  <Button type="submit" disabled={waitlistLoading} className="h-11 px-6 shrink-0">
                    {waitlistLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Request Access
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Button>
                </form>
                <p className="text-[11px] text-muted-foreground">
                  We're onboarding teams in batches. Join the waitlist to get early access.
                </p>
              </div>
            ) : (
              <>
                <nav className="flex flex-col sm:flex-row items-center justify-center gap-3" aria-label="Primary actions">
                  <Button onClick={() => navigate("/login")} className="h-11 px-6">
                    Start Tracking Revenue — $499/mo
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/login")}
                    className="h-11 px-6"
                  >
                    <BarChart3 className="mr-2 h-4 w-4" aria-hidden="true" />
                    Sign In
                  </Button>
                </nav>
                <p className="mt-4 text-[11px] text-muted-foreground">
                  No credit card required to explore. Cancel anytime.
                </p>
              </>
            )}
          </motion.article>
        </section>

        {/* How It Works */}
        <section className="max-w-4xl mx-auto px-4 py-16" aria-labelledby="how-it-works-heading">
          <div className="text-center mb-12">
            <h2 id="how-it-works-heading" className="text-2xl font-semibold mb-2">How It Works</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Three steps from signup to a complete revenue analytics dashboard.
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="grid gap-8 md:grid-cols-3"
            role="list"
          >
            {steps.map((step, i) => (
              <motion.article
                key={i}
                variants={itemVariants}
                className="relative flex flex-col items-center text-center rounded-xl border border-border bg-card p-6"
                role="listitem"
              >
                {/* Step number */}
                <span className="absolute -top-3 left-4 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold" aria-label={`Step ${i + 1}`}>
                  {i + 1}
                </span>

                <div className="mb-4 h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <step.icon className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>

                <h3 className="text-sm font-semibold mb-2">{step.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>

                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 border-t border-dashed border-border" aria-hidden="true" />
                )}
              </motion.article>
            ))}
          </motion.div>
        </section>

        {/* FAQ Section — AEO / GEO optimized */}
        <section className="max-w-2xl mx-auto px-4 pb-20" aria-labelledby="faq-heading">
          <div className="flex items-center gap-2 mb-6">
            <HelpCircle className="h-5 w-5 text-primary" aria-hidden="true" />
            <h2 id="faq-heading" className="text-lg font-semibold">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-sm font-medium">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" aria-hidden="true" />
            <span className="font-medium text-foreground">TrueMRR</span>
            <span>· SaaS revenue analytics dashboard</span>
          </div>
          <nav aria-label="Footer navigation" className="flex items-center gap-4">
            <button onClick={() => navigate("/help")} className="hover:text-foreground transition-colors">Help Center</button>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/refunds" className="hover:text-foreground transition-colors">Refunds</Link>
            <a href="mailto:sameh@gettruemrr.com" className="hover:text-foreground transition-colors">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Index;